import { Button, Divider } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import Logger from 'browser-logging/src/browser/Logger';

import { ApiError } from 'common/src/exceptions/ErrorHandling';
import { IFacility, IFacilityGroup } from 'common/src/types/interfaces';

import { getFacilities } from 'portal/src/api/FacilitiesService';
import {
  addFacilityGroup,
  updateFacilityGroup,
} from 'portal/src/api/FacilityGroupsService';
import useAuth from 'portal/src/auth/useAuth';
import DataTable from 'portal/src/components/common/DataTable';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { formControlErrors } from 'portal/src/components/common/forms/formControlErrors';
import GenericDialog from 'portal/src/components/common/GenericDialog';
import TransferList, {
  TransferListItem,
} from 'portal/src/components/common/TransferList';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import { ValidationError } from 'portal/src/utils/ValidationError';

import { defaultValues, getFields } from './FacilityGroupForm.fields';

import useSnackbarHandler from './../../../../utils/Snackbar/useSnackbarHandler';

const facilityGridColumns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Facility name',
    flex: 1,
  },
  { field: 'type', headerName: 'Facility type', minWidth: 240 },
];

interface FormComponentProps {
  entity?: Partial<IFacilityGroup>;
  handleBackAction: () => void;
}

const FacilityGroupForm = ({
  entity = {},
  handleBackAction,
}: FormComponentProps) => {
  const snackbarHandler = useSnackbarHandler();

  const [selectedFacilities, setSelectedFacilities] = useState<
    Array<IFacility>
  >([]);
  const [facilitiesChanged, setFacilitiesChanged] = useState(false);
  const [openFacilitiesDialog, setOpenFacilitiesDialog] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    ...formControl
  } = useForm({ defaultValues: defaultValues(entity) });
  const { getUser, hasReadOnlyAccess } = useAuth();
  const { organizationId } = getUser();

  const {
    data: facilities,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['facilityGroupsForm'],
    queryFn: fetchFacilities,
    initialData: [],
  });

  async function fetchFacilities(): Promise<IFacility[]> {
    try {
      const { data } = await getFacilities();
      if (entity.id && entity.facilities) {
        const selected = entity.facilities.reduce((acc, facilityInGroup) => {
          const facilityFound = data.find(
            (facility) => facilityInGroup.id === facility.id,
          );
          facilityFound && acc.push(facilityFound);
          return acc;
        }, []);
        setSelectedFacilities(selected);
      }
      return data.filter((facility) => facility.isActive);
    } catch (err) {
      Logger.captureError(err as Error);
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    }
  }

  const handleSendData = async (payload) => {
    try {
      if (!selectedFacilities.length) {
        snackbarHandler.error(MESSAGES.FACILITY.ONE_REQUIRED);
        return;
      }

      const formattedPayload = {
        ...payload,
        organizationId,
        facilities: selectedFacilities,
      };

      async function add() {
        await addFacilityGroup(formattedPayload);
        handleBackAction();
        snackbarHandler.success(MESSAGES.FACILITY_GROUP.CREATED);
      }

      async function update() {
        await updateFacilityGroup(entity.id, formattedPayload);
        handleBackAction();
        snackbarHandler.success(MESSAGES.FACILITY_GROUP.UPDATED);
      }

      await (entity.id ? update() : add());
    } catch (err: any) {
      if (err instanceof ValidationError) {
        snackbarHandler.error(err.errors.join('; \n'));
      } else if (ApiError.compatible(err)) {
        formControlErrors(formControl, err);
      } else {
        Logger.captureError(err as Error);
        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      }
    }
  };

  const handleSaveFacilitiesPickerDialog = (
    resultItems: readonly TransferListItem[],
  ) => {
    const newSelected = resultItems.reduce((acc, item) => {
      const facility = facilities.find((e) => e.id === item.value);
      if (facility) {
        acc.push(facility);
      }
      return acc;
    }, []);
    setSelectedFacilities(newSelected);
    setFacilitiesChanged(true);
    setOpenFacilitiesDialog(false);
  };

  const facilitiesPickerSelectedItems = selectedFacilities.map((e) => ({
    value: e.id,
    label: e.name,
  }));

  const facilitiesPickerAvailableItems = facilities.map((e) => ({
    value: e.id,
    label: e.name,
  }));

  return (
    <FormContainer
      control={control}
      handleBackAction={handleBackAction}
      handleSubmit={handleSubmit((data) => handleSendData(data))}
      header="Facility Group Information"
      isDirty={isDirty || facilitiesChanged}
      isNew={!entity.id}
      isPending={false}
      mainFieldsConfig={getFields()}
      afterMainFields={
        <>
          <Divider>Associated Facilities</Divider>
          <Button
            color="secondary"
            disabled={hasReadOnlyAccess() || isFetching}
            data-testid={'select-facilities-form'}
            onClick={() => setOpenFacilitiesDialog(true)}
            sx={{ width: 'fit-content' }}
            variant="outlined"
          >
            {`Select Facilities (${selectedFacilities.length})`}
          </Button>
          <DataTable
            columns={facilityGridColumns}
            checkboxSelection={false}
            dataFetchError={isError}
            isRowNavigation={false}
            loading={isFetching}
            rows={selectedFacilities}
          />
          <GenericDialog
            title="Select Facilities"
            open={openFacilitiesDialog}
            onClose={() => setOpenFacilitiesDialog(false)}
          >
            <TransferList
              items={facilitiesPickerAvailableItems}
              selectedItems={facilitiesPickerSelectedItems}
              onSave={handleSaveFacilitiesPickerDialog}
              onCancel={() => setOpenFacilitiesDialog(false)}
            />
          </GenericDialog>
        </>
      }
    />
  );
};

export default FacilityGroupForm;
